/**
 * AnimatedTabBar — web port of mobile components/navigation/AnimatedTabBar.tsx.
 *
 * Phone-width browsers only (the shell hides it at md+ and shows the sidebar
 * instead). A floating pill (radius 28, 24px side gutter, capped like the
 * app at Layout.contentMaxWidth) with the illustrated clay tab art, a sliding
 * faint focus pill, labels under the icons, and the raised centre "+" that
 * opens the Add Food sheet and turns into "×" (45° spin) while it's open.
 *
 * Like the app, it only shows on the four tab screens — stack screens
 * (recipe detail, weight, history, settings sub-pages…) cover it.
 */

import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import clsx from 'clsx'
import { Ionicon } from '@/components/ui/Ionicon'
import { TAB_IMAGES, TAB_ICON_SIZE, INACTIVE_ICON_OPACITY, TAB_LABEL_KEYS } from '@/constants/icons'
import { Colors } from '@/theme/tokens'
import { withAlpha } from '@/theme/color'

export type TabKey = 'index' | 'recipes' | 'progress' | 'settings'

/** Mobile tab route name → web path, in tab order (Diary, Recipes | + | Progress, Settings). */
export const TAB_ROUTES: { key: TabKey; to: string }[] = [
  { key: 'index', to: '/diary' },
  { key: 'recipes', to: '/recipes' },
  { key: 'progress', to: '/progress' },
  { key: 'settings', to: '/settings' },
]

/** Index of the tab whose screen is showing, or -1 on a stack screen (bar hidden). */
export function tabIndexForPath(pathname: string): number {
  const path = pathname.replace(/\/+$/, '') || '/'
  return TAB_ROUTES.findIndex((r) => r.to === path)
}

// AnimatedTabBar StyleSheet / layout constants.
const CENTER_BUTTON_TOTAL_WIDTH = 80 // 72 ring + 4*2 marginHorizontal
const INDICATOR_HORIZONTAL_MARGIN = 8
const TAB_COUNT = TAB_ROUTES.length

// Elevation (iOS shadow 0/8/24 @ 0.12, black) and the centre face's lift
// (shadowColor #0D6B4B = light primaryDark, 0/4/4 @ 0.15).
const TAB_BAR_SHADOW = '0px 8px 24px rgba(0,0,0,0.12)'
const CENTER_FACE_SHADOW = `0px 4px 4px ${withAlpha(Colors.light.primaryDark, 0.15)}`

// Spring-ish easing for the indicator slide (damping 15 / stiffness 150) and
// the icon/centre-button bounces.
const SLIDE_EASE = 'cubic-bezier(0.34, 1.32, 0.64, 1)'

/** `left` of the focus pill for tab i: tabs 2+ sit after the centre button gap. */
function indicatorLeft(i: number): string {
  const tabWidth = `((100% - ${CENTER_BUTTON_TOTAL_WIDTH}px) / ${TAB_COUNT})`
  const gap = i >= 2 ? CENTER_BUTTON_TOTAL_WIDTH : 0
  return `calc(${i} * ${tabWidth} + ${INDICATOR_HORIZONTAL_MARGIN + gap}px)`
}

function TabItem({ tab, focused }: { tab: (typeof TAB_ROUTES)[number]; focused: boolean }) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const label = t(TAB_LABEL_KEYS[tab.key])

  return (
    <button
      type="button"
      role="tab"
      aria-selected={focused}
      aria-label={label}
      onClick={() => {
        if (!focused) navigate(tab.to)
      }}
      className="flex min-w-0 flex-1 flex-col items-center justify-center py-xs transition-transform duration-150 active:scale-90"
    >
      <span
        className="block"
        style={{
          width: TAB_ICON_SIZE,
          height: TAB_ICON_SIZE,
          transform: `scale(${focused ? 1.15 : 1})`,
          transition: `transform 300ms ${SLIDE_EASE}`,
        }}
      >
        <img
          src={TAB_IMAGES[tab.key]}
          alt=""
          aria-hidden
          draggable={false}
          className="select-none object-contain"
          style={{ width: TAB_ICON_SIZE, height: TAB_ICON_SIZE, opacity: focused ? 1 : INACTIVE_ICON_OPACITY }}
        />
      </span>
      {/* fontSize 10 / lineHeight 12 / medium / letterSpacing 0.1 / marginTop 2 */}
      <span
        className={clsx(
          'mt-[2px] block max-w-full truncate text-center font-medium',
          focused ? 'text-primary' : 'text-text-secondary',
        )}
        style={{ fontSize: 10, lineHeight: '12px', letterSpacing: 0.1 }}
      >
        {label}
      </span>
    </button>
  )
}

function CenterAddButton({ isOpen, onPress }: { isOpen: boolean; onPress: () => void }) {
  const { t } = useTranslation()
  return (
    // marginHorizontal 4 / marginTop -32: the ring overhangs the bar's top edge.
    <div className="mx-xs flex items-center justify-center" style={{ marginTop: -32 }}>
      <button
        type="button"
        onClick={onPress}
        aria-label={t('diary.addFood.title')}
        aria-expanded={isOpen}
        className="group flex h-[72px] w-[72px] items-center justify-center rounded-full bg-bg-elevated"
      >
        {/* press: scale 0.85 → spring back */}
        <span className="block transition-transform duration-200 group-active:scale-[0.85]">
          <span
            className="flex h-[58px] w-[58px] items-center justify-center overflow-hidden rounded-full bg-button-primary-strong text-button-primary-text"
            style={{
              boxShadow: CENTER_FACE_SHADOW,
              opacity: isOpen ? 0.85 : 1,
              transform: `rotate(${isOpen ? 45 : 0}deg)`,
              transition: `transform 300ms ${SLIDE_EASE}, opacity 200ms ease`,
            }}
          >
            <Ionicon name="add" size={30} />
          </span>
        </span>
      </button>
    </div>
  )
}

export function AnimatedTabBar({
  activeIndex,
  isAddOpen,
  onCenterPress,
}: {
  /** Focused tab (0–3). */
  activeIndex: number
  /** Add Food sheet open → the centre button shows ×. */
  isAddOpen: boolean
  onCenterPress: () => void
}) {
  const left = TAB_ROUTES.slice(0, 2)
  const right = TAB_ROUTES.slice(2)
  const tabWidth = `((100% - ${CENTER_BUTTON_TOTAL_WIDTH}px) / ${TAB_COUNT})`

  return (
    <div
      className="no-print pointer-events-none fixed inset-x-0 bottom-0 z-40 flex justify-center px-lg md:hidden"
      // paddingBottom: insets.bottom > 0 ? insets.bottom : 20
      style={{ paddingBottom: 'max(env(safe-area-inset-bottom), 20px)' }}
    >
      <nav
        role="tablist"
        className="pointer-events-auto relative flex w-full max-w-[552px] rounded-[28px] bg-bg-elevated py-smd"
        style={{ boxShadow: TAB_BAR_SHADOW }}
      >
        {/* Sliding focus pill — a single faint wash (primary @ 7% light / 10% dark) */}
        <span
          aria-hidden
          className="absolute bottom-[10px] top-[10px] z-[1] overflow-hidden rounded-[16px] bg-primary/[0.07] dark:bg-primary/10"
          style={{
            left: indicatorLeft(Math.max(0, activeIndex)),
            width: `calc(${tabWidth} - ${INDICATOR_HORIZONTAL_MARGIN * 2}px)`,
            transition: `left 350ms ${SLIDE_EASE}`,
          }}
        />

        <div className="relative z-[2] flex flex-1">
          {left.map((tab, i) => (
            <TabItem key={tab.key} tab={tab} focused={activeIndex === i} />
          ))}
          <CenterAddButton isOpen={isAddOpen} onPress={onCenterPress} />
          {right.map((tab, i) => (
            <TabItem key={tab.key} tab={tab} focused={activeIndex === i + 2} />
          ))}
        </div>
      </nav>
    </div>
  )
}

export default AnimatedTabBar
